/** Identity seam for D&D Queue — mirrors rukh-pd-queue/src/services/identity.ts */
import { useAuthStore } from '@/stores/auth'
import type { User } from '@/types/auth'

export interface DdOperator {
  id: string
  name: string
  email: string
  role: string
  team: string
}

/**
 * The prototype's stand-in identity. Fixtures and tests only: in the app the operator is
 * the signed-in user (see ddQueueCurrentOperator), so nothing is attributed to this name.
 */
const DEFAULT_OPERATOR: DdOperator = {
  id: 'u-taylor-whitney',
  name: 'Taylor Whitney',
  email: 'taylor.whitney@fastsigns.com',
  role: 'Director, AI & Automation Strategy',
  team: 'Visual Solutions Group'
}

/** Set explicitly (tests, demos) — wins over the signed-in user until reset. */
let explicitOperator: DdOperator | null = null

let cachedUser: User | null = null
let cachedOperator: DdOperator | null = null

export function setDdQueueOperator(operator: DdOperator): void {
  explicitOperator = operator
}

export function resetDdQueueOperator(): void {
  explicitOperator = null
}

/**
 * The person acting in the D&D Queue — what the local activity rows ("… uploaded a
 * proof"), "My tasks", the KPI "mine" count, the chat's own-message styling and the
 * assignee gate compare against.
 *
 * F-093: this used to be the prototype's stand-in for everyone, so every upload read
 * "Taylor Whitney uploaded …" whoever did it. Now: an explicitly set operator, else the
 * signed-in user, else (no session — fixtures, tests without auth) the stand-in.
 */
export function ddQueueCurrentOperator(): DdOperator {
  if (explicitOperator) return explicitOperator
  return signedInOperator() ?? { ...DEFAULT_OPERATOR }
}

function signedInOperator(): DdOperator | null {
  let user: User | null
  try {
    user = useAuthStore().user
  } catch {
    return null // no active Pinia (pure-lib tests)
  }
  const name = user?.name?.trim()
  if (!user || !name) return null
  if (user === cachedUser && cachedOperator) return cachedOperator
  cachedUser = user
  cachedOperator = {
    id: user.uuid ?? String(user.id),
    name,
    email: user.email ?? '',
    role: '',
    team: ''
  }
  return cachedOperator
}
